import type { RetrievedChunkSnapshot } from "../db/types";
import { tr } from "../i18n/i18n.svelte";

export interface CitationSpan {
  kind: "text" | "citation";
  text: string;
  /** For citations: the retrieved chunks this [N] or [N, M] refers to. */
  refs?: RetrievedChunkSnapshot[];
  /** For citations: the LLM-picked verbatim quote from the chunk (only with single-ref form). */
  quote?: string;
}

/** Matches [N: "quote"] (single ref with quote) or [N] / [N, M] (plain).
 *  Group 1 = single ref + quote case : number
 *  Group 2 = single ref + quote case : quote text
 *  Group 3 = plain case : one or more comma-separated numbers
 */
export const CITATION_RE =
  /\[(\d+)\s*:\s*"((?:[^"\\]|\\")*)"\]|\[(\d+(?:\s*,\s*\d+)*)\]/g;

export interface ParsedCitation {
  refs: number[];
  quote: string | null;
}

export function parseCitationMatch(
  m: RegExpMatchArray | RegExpExecArray,
): ParsedCitation {
  if (m[1] !== undefined) {
    return {
      refs: [Number(m[1])],
      quote: (m[2] ?? "").replace(/\\"/g, '"').trim() || null,
    };
  }
  const nums = (m[3] ?? "").split(/\s*,\s*/).map(Number);
  return { refs: nums, quote: null };
}

/** Split a response into (text | citation) spans. Unknown numbers fall back to text. */
export function splitCitations(
  content: string,
  retrieved: RetrievedChunkSnapshot[],
): CitationSpan[] {
  const byNumber = new Map<number, RetrievedChunkSnapshot>();
  for (const r of retrieved) byNumber.set(r.number, r);

  const spans: CitationSpan[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  const re = new RegExp(CITATION_RE.source, "g");
  while ((match = re.exec(content)) !== null) {
    const parsed = parseCitationMatch(match);
    const refs = parsed.refs
      .map((n) => byNumber.get(n))
      .filter((r): r is RetrievedChunkSnapshot => !!r);

    if (refs.length === 0) continue;

    if (match.index > lastIndex) {
      spans.push({ kind: "text", text: content.slice(lastIndex, match.index) });
    }
    spans.push({
      kind: "citation",
      text: match[0],
      refs,
      quote: parsed.quote ?? undefined,
    });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    spans.push({ kind: "text", text: content.slice(lastIndex) });
  }

  return spans;
}

export interface SourceMeta {
  icon: string;
  label: string;
}

/** Icon + short label for a chunk's provenance. Local (the user's own docs) is
 *  the default; the others come from the EPFL hybrid search. */
export function sourceMeta(source?: RetrievedChunkSnapshot["source"]): SourceMeta {
  switch (source) {
    case "epfl_site":
      return { icon: "🌐", label: "EPFL" };
    case "servicenow_kb":
      return { icon: "📚", label: "KB" };
    case "servicenow_incident":
      return { icon: "🎫", label: "INC" };
    case "web":
      return { icon: "🔎", label: "Web" };
    default:
      return { icon: "📄", label: tr({ fr: "perso", en: "personal" }) };
  }
}

/** Short display label for a single ref : "DocName p.12" or "DocName §section".
 *  External (EPFL/ServiceNow) sources are prefixed with their icon and have no
 *  meaningful page number, so they show the icon + name (+ section if any). */
export function refLabel(ref: RetrievedChunkSnapshot): string {
  const section = ref.section?.split(" > ").pop();
  if (ref.source && ref.source !== "local") {
    const { icon } = sourceMeta(ref.source);
    return section ? `${icon} ${ref.docName} §${section}` : `${icon} ${ref.docName}`;
  }
  if (ref.page > 1 || !section) return `${ref.docName} p.${ref.page}`;
  return `${ref.docName} §${section}`;
}
